import { Config } from '../../../../video-data/config';
import { CanvasWordToken } from './CanvasWordToken';
import { CanvasTextMetricList } from './CanvasTextMetricList';
import type { CanvasWordElementMetrics } from './CanvasWordElementMetrics';

// in line = "hello, my name is"
// end of line = "this is the end."
const IN_LINE_PUNCTUATIONS = '.!?:;。！？：；';
const END_OF_LINE_PUNCTUATIONS = ':;,：；、，';

/**
 * CanvasWordMetrics is a class that represents the measurement of a word.
 */
export class CanvasWordMetrics extends CanvasTextMetricList<CanvasWordElementMetrics> {
    get elements(): CanvasWordElementMetrics[] {
        return this.children;
    }

    slice(start: number, end: number): CanvasWordToken {
        // clamp values to bounds of the internal text length
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.text.length);
        let gIndex = 0;

        const slicedWordToken = new CanvasWordToken();

        this.elements.forEach((element) => {
            const wordElStart = clampedStart - gIndex;
            const wordElEnd = clampedEnd - gIndex;
            const numChars = element.text.length;

            if (wordElStart < numChars && wordElEnd > 0) {
                const slicedWordElementToken = element.slice(wordElStart, wordElEnd);
                slicedWordToken.add(slicedWordElementToken);
            }
            gIndex += numChars;
        });

        return slicedWordToken;
    }

    /**
     * Reference index to the source text that this token is a part of.
     */
    get sourceWordReferenceIndex() {
        if (this.elements.length === 0) {
            throw new Error('Cannot get sourceWordReferenceIndex, no word elements exist');
        }
        return this.elements[0].sourceWordReferenceIndex;
    }

    override _getSourceWordReferenceIndices(): never {
        throw new Error('CanvasWordMetrics does not support getSourceWordReferenceIndices');
    }

    setReadingDirection(lineOffsetX: number, advanceXOffset: number) {
        let advanceX = advanceXOffset;
        this.elements.forEach((wordElement) => {
            if (this.style.readingDirection === Config.READING_DIRECTION.LTR) {
                wordElement.x = lineOffsetX + wordElement.x;
                wordElement.advanceX = advanceX;
                advanceX += wordElement.advanceWidth;
            } else if (this.style.readingDirection === Config.READING_DIRECTION.RTL) {
                wordElement.x = lineOffsetX - wordElement.advanceWidth - wordElement.x;
                wordElement.advanceX = lineOffsetX - advanceX - wordElement.advanceWidth;
                advanceX += wordElement.advanceWidth;
            } else {
                throw new Error(`Unrecognized readingDirection: ${this.style.readingDirection}`);
            }
        });
        this.clearCachedValues();
    }

    resetWordElementMetrics() {
        this.clearCachedValues();
        this.elements.forEach((element) => element.hide());
    }

    endsWithInLinePunctuation(): boolean {
        return !!this.text.match(`[${IN_LINE_PUNCTUATIONS}]$`);
    }

    endsWithEndOfLinePunctuation(): boolean {
        return !!this.text.match(`[${END_OF_LINE_PUNCTUATIONS}]$`);
    }

    removeLastCharacter() {
        if (this.elements.length === 0) {
            throw new Error('Cannot remove last character from an empty word');
        }
        this.elements[this.elements.length - 1].removeLastCharacter();
        this.clearCachedValues();
    }
}
